import { createContext, useCallback, useContext, useMemo, useRef } from "react";

import { stringIds } from "#app/lib/forms";
import {
  type PendingMediaUpload,
  usePendingMediaUploads,
} from "#app/lib/hooks/use-pending-media-uploads";

/** Photo ids compose from the latest list, not a render snapshot: two uploads landing in one tick would append to the same stale array. */
export type AnswerUpdate = (itemId: string, next: (previous: unknown) => unknown) => void;

/** Uploads live with the answers, not the question card: a paged form unmounts the card on Next and would cancel them. */
export type FormPhotoUploads = {
  pending: readonly PendingMediaUpload[];
  add: (itemId: string, files: File[], single: boolean) => void;
  cancel: (tempId: string) => void;
  updateAnswer: AnswerUpdate;
};

export function useFormPhotoUploads(updateAnswer: AnswerUpdate): FormPhotoUploads {
  const singles = useRef(new Set<string>());
  const { pending, startUploads, cancel } = usePendingMediaUploads({
    onUploaded: ({ photoId }, itemId) => {
      if (!itemId) return;
      updateAnswer(itemId, (previous) =>
        singles.current.has(itemId) ? [photoId] : [...stringIds(previous), photoId],
      );
    },
  });
  const add = useCallback(
    (itemId: string, files: File[], single: boolean) => {
      if (single) singles.current.add(itemId);
      else singles.current.delete(itemId);
      void startUploads(files, itemId);
    },
    [startUploads],
  );
  return useMemo(
    () => ({ add, cancel, pending, updateAnswer }),
    [add, cancel, pending, updateAnswer],
  );
}

const FormPhotoUploadsContext = createContext<FormPhotoUploads | null>(null);

export const FormPhotoUploadsProvider = FormPhotoUploadsContext.Provider;

export function useFormPhotoUploadsContext(): FormPhotoUploads | null {
  return useContext(FormPhotoUploadsContext);
}
